import pillPlusLogo from "../../pillplus-logo.png";
import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "../../lib/api";
import type { Changelog } from "../../lib/types";
import { ITEMS_PER_PAGE, TAG_COLORS } from "../../lib/constants";

export default function ChangeUpdates() {
  const [changelogs, setChangelogs] = useState<Changelog[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const loaderRef = useRef<HTMLDivElement>(null);

  const loadMoreChangelogs = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const { changeLogs } = await api.getChangeLogs(
        page,
        ITEMS_PER_PAGE,
        true,
      );
      if (changeLogs.length < ITEMS_PER_PAGE) {
        setHasMore(false);
      }
      setChangelogs(prev => [...prev, ...changeLogs]);
      setPage(prev => prev + 1);
    } catch (error) {
      console.error("Error loading changelogs:", error);
    } finally {
      setLoading(false);
    }
  }, [page, loading, hasMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          loadMoreChangelogs();
        }
      },
      { threshold: 0.1 },
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [loadMoreChangelogs]);

  return (
    <div className="flex flex-col items-center w-full max-w-6xl mx-auto p-4">
      <div className="flex flex-col items-center justify-center py-8 fixed top-0 left-0 right-0 bg-white z-20">
        <img src={pillPlusLogo} alt="PillPlus Logo" className="mb-4" />
        <div className="flex flex-col items-center justify-center mb-4">
          <h1 className="text-2xl font-bold">Updates</h1>
          <p className="text-sm text-gray-500">
            Here you can see the latest updates about PillPlus.
          </p>
        </div>
      </div>

      <div className="w-full mt-64">
        {changelogs.map(changelog => (
          <div key={changelog.id} className="flex w-full mb-12">
            {/* Left side - Date and Version */}
            <div className="w-[30%] pr-8 text-right">
              <p className="font-semibold text-gray-700">
                {new Date(changelog.release_date).toLocaleDateString()}
              </p>
              <p className="text-sm text-gray-500">
                Version {changelog.version}
              </p>
            </div>

            {/* Timeline line */}
            <div className="relative flex items-center justify-center px-4">
              <div className="h-full absolute w-px bg-gray-300" />
              <div className="relative z-10 w-3 h-3 rounded-full bg-blue-500" />
            </div>

            {/* Right side - Content */}
            <div className="flex-1 pl-8">
              {changelog.content.map((section, sectionIndex) => (
                <div
                  key={`${changelog.id}-${sectionIndex}`}
                  className="mb-6 last:mb-0"
                >
                  <h3 className="text-lg font-semibold mb-3">
                    {section.title}
                  </h3>
                  <ul className="space-y-2">
                    {section.descriptions.map((desc, descIndex) => (
                      <li
                        key={`${changelog.id}-${sectionIndex}-${descIndex}`}
                        className="flex items-start gap-2"
                      >
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                            TAG_COLORS[desc.tag as keyof typeof TAG_COLORS]
                          }`}
                        >
                          {desc.tag}
                        </span>
                        <span className="text-gray-700">{desc.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ))}
        {!loading && (
          <div className="text-gray-500 text-center">That's all folks!</div>
        )}

        {/* Loader reference element */}
        <div ref={loaderRef} className="h-10 w-full flex justify-center">
          {loading && (
            <div className="text-gray-500">Loading more updates...</div>
          )}
        </div>
      </div>
    </div>
  );
}
